<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { sessionHelper } from "@/utils";
import { SHARED } from "@/constants";
import { mapState } from "vuex";
import { LOOKUP_MODULE } from "@/state/modules/lookup";
import { stockBalanceMethods, lookupMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

const PURPLES = ["#7C4DFF", "#9C6AFF", "#5E2FE0", "#B388FF", "#651FFF", "#6200EA", "#3D5AFE", "#536DFE", "#C9B8FF"];

export default {
  page: { title: i18n.t("label.listingX", [i18n.t("module.stockBalance")]) },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      title: i18n.t("label.listingX", [i18n.t("module.stockBalance")]),
      currentPage: 1,
      filter: "",
      perPage: SHARED.DEFAULT_PAGE_SIZE,
      fields: [
        { key: 'itemCode', sortable: true, label: i18n.t('label.itemCode') },
        { key: 'location', sortable: true, label: i18n.t('label.location') },
        { key: 'uom', sortable: true, label: i18n.t('label.uom') },
        { key: 'balanceQty', sortable: true, label: 'Balance Qty' },
        { key: 'balanceCost', sortable: true, label: 'Unit Cost' },
        { key: 'totalValue', sortable: true, label: 'Total Value' },
      ],
      searchCriteria: {},
      selectedItem: null,
    };
  },
  computed: {
    ...mapState("stockBalance", ["status"]),
    ...mapState(LOOKUP_MODULE, ["itemDdl"]),
    tableRows() { return this.$store.state.stockBalance.list.data; },
    pagination() { return this.$store.state.stockBalance.list.pagination; },

    // KPIs scope: when an item is picked, restrict to its rows; otherwise use everything on this page.
    scopedRows() {
      if (!this.selectedItem) return this.tableRows;
      return this.tableRows.filter((r) => r.itemCode === this.selectedItem.item_code);
    },
    totalValue() {
      return this.scopedRows.reduce((s, r) => s + (r.totalValue || 0), 0);
    },
    totalQty() {
      return this.scopedRows.reduce((s, r) => s + (r.balanceQty || 0), 0);
    },
    locationCount() {
      return new Set(this.scopedRows.map((r) => r.location).filter(Boolean)).size;
    },
    negativeCount() {
      return this.scopedRows.filter((r) => (r.balanceQty || 0) < 0).length;
    },
    byLocation() {
      const map = {};
      for (const r of this.scopedRows) {
        const loc = r.location || "—";
        if (!map[loc]) map[loc] = { qty: 0, value: 0 };
        map[loc].qty += r.balanceQty || 0;
        map[loc].value += r.totalValue || 0;
      }
      return Object.entries(map).map(([loc, v]) => ({ location: loc, ...v }));
    },
    locationChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        labels: this.byLocation.map((r) => r.location),
        colors: PURPLES,
        legend: { position: "bottom", fontSize: "12px" },
        plotOptions: { pie: { donut: { size: "65%" } } },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` } },
      };
    },
    locationChartSeries() {
      return this.byLocation.map((r) => Math.max(r.qty, 0));
    },
  },
  async created() {
    this.searchCriteria = { pageNumber: SHARED.DEFAULT_PAGE_NUMBER, pageSize: SHARED.DEFAULT_PAGE_SIZE, searchArray: [] };
    await Promise.all([
      this.getList(this.searchCriteria),
      this.lookupItem({}),
    ]);
  },
  methods: {
    ...stockBalanceMethods,
    ...lookupMethods,
    getList(filter) {
      this.getAllStockBalances({ filter });
      this.searchCriteria = filter;
      sessionHelper.storeSearch("STOCK_BALANCE_SEARCH", filter);
    },
    pageNavigation(pageNumber) {
      this.searchCriteria.pageNumber = pageNumber;
      this.getList(this.searchCriteria);
    },
    searchListing() {
      this.searchCriteria.pageNumber = SHARED.DEFAULT_PAGE_NUMBER;
      this.searchCriteria.searchArray = [this.filter];
      this.getList(this.searchCriteria);
    },
    onItemPick(it) {
      this.selectedItem = it;
    },
    fmtRm(v) {
      const n = Number(v) || 0;
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtQty(v) {
      const n = Number(v) || 0;
      return n.toLocaleString();
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" />

    <!-- Product picker -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <label class="d-block mb-1 small text-muted">{{ $t('label.itemCode') }}</label>
            <multiselect
              :value="selectedItem"
              :options="itemDdl"
              :custom-label="(o) => `${o.item_code} — ${o.description || ''}`"
              track-by="item_code"
              :show-labels="false"
              :allow-empty="true"
              :placeholder="'All items (clear to see overall)'"
              @input="onItemPick"
            />
            <small class="text-muted">
              <i class="mdi mdi-information-outline mr-1"></i>
              Pick a product to see KPIs and breakdown for just that item.
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- KPI cards -->
    <div class="row">
      <div class="col-6 col-md-3">
        <div class="card kpi">
          <div class="card-body">
            <div class="kpi-label">Total Value</div>
            <div class="kpi-value">RM {{ fmtRm(totalValue) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card kpi">
          <div class="card-body">
            <div class="kpi-label">Total Qty</div>
            <div class="kpi-value">{{ fmtQty(totalQty) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card kpi">
          <div class="card-body">
            <div class="kpi-label">Locations</div>
            <div class="kpi-value">{{ locationCount }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card kpi" :class="{ 'kpi-warn': negativeCount > 0 }">
          <div class="card-body">
            <div class="kpi-label">Negative Rows</div>
            <div class="kpi-value">{{ negativeCount }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Chart + Table -->
    <div class="row">
      <div class="col-12 col-lg-4">
        <div class="card h-100">
          <div class="card-body">
            <h6 class="card-title">Stock by Location</h6>
            <apexchart v-if="byLocation.length" type="donut" height="260" :options="locationChartOptions" :series="locationChartSeries" />
            <div v-else class="text-muted text-center py-5">No data</div>
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-8">
        <div class="card h-100">
          <div class="card-body">
            <div class="row mt-2 mb-3">
              <div class="col-sm-12 col-md-6">
                <label class="d-inline-flex align-items-center">
                  {{ $t('label.search') }}
                  <b-form-input v-model="filter" type="search" class="form-control form-control-sm ml-2" @change="searchListing"></b-form-input>
                </label>
              </div>
            </div>
            <div class="table-responsive mb-0">
              <b-table :items="scopedRows" :fields="fields" responsive="sm" :per-page="perPage" small>
                <template #cell(balanceQty)="data">
                  <span :class="{ 'text-danger font-weight-bold': data.item.balanceQty < 0 }">
                    {{ fmtQty(data.item.balanceQty) }}
                  </span>
                </template>
                <template #cell(balanceCost)="data">{{ fmtRm(data.item.balanceCost) }}</template>
                <template #cell(totalValue)="data">{{ fmtRm(data.item.totalValue) }}</template>
                <template #cell(location)="data">
                  <b-badge variant="light" class="font-monospace">{{ data.item.location }}</b-badge>
                </template>
              </b-table>
            </div>
            <div class="row" v-if="!selectedItem">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="pagination.total" :per-page="perPage" @change="pageNavigation"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.kpi { border: 1px solid #e9ecef; }
.kpi .card-body { padding: 16px; }
.kpi-label { font-size: 11px; color: #8993A4; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.kpi-value { font-size: 22px; font-weight: 700; color: #212529; }
.kpi-warn .kpi-value { color: #dc3545; }
</style>
